import * as Sentry from '@sentry/nextjs';

import { setRequestObserver } from '@cineamo/api/fetch-helper';

import { describeBookingError, type ErrorLike } from './booking-telemetry-helpers';

export { describeBookingError, paymentIntentIdOf } from './booking-telemetry-helpers';

/**
 * What a booking failure is reported with (CIN-15436), shared by every booking flow (web, cinema websites,
 * embedded). This file is the part that is safe on the server too (server actions): Sentry only. The browser
 * half, which adds PostHog, is `booking-telemetry-client.ts`.
 *
 * The keys are the correlation keys the backend tags as well (cart_uuid, order_id, payment_intent, cinema_id,
 * request_id), so one search finds a checkout in Sentry, PostHog and CloudWatch.
 */
export type BookingContext = {
    cart_uuid?: string;
    showing_id?: number | string;
    cinema_id?: number | string;
    payment_intent?: string;
    order_id?: number | string;
    client?: string;
};

let context: BookingContext = {};
let lastRequestId: string | undefined;

// The id of every traced API call goes onto the Sentry scope, so an error captured after it carries the id of the
// call before it. On the server the isolation scope is per request; the module variable below is only for the
// browser, where there is one buyer (on the server it would leak between concurrent requests).
setRequestObserver(({ requestId }) => {
    if (typeof window !== 'undefined') lastRequestId = requestId;
    Sentry.getIsolationScope().setTag('request_id', requestId);
});

/** The id of the last traced request this browser sent; undefined on the server and before the first one. */
export function getLastRequestId(): string | undefined {
    return lastRequestId;
}

export function getBookingContext(): BookingContext {
    return context;
}

function definedTags(values: Record<string, unknown>): Record<string, string> {
    const tags: Record<string, string> = {};
    for (const [key, value] of Object.entries(values)) {
        if (value !== undefined && value !== null && value !== '') tags[key] = String(value);
    }
    return tags;
}

/** Remembers what is known about the booking and puts it on the Sentry scope; later calls add to it. */
export function setBookingContext(update: BookingContext): void {
    context = { ...context, ...update };
    Sentry.setTags(definedTags(update));
    Sentry.setContext('booking', context);
}

/** Leaving the booking: its ids must not stay on the scope of whatever the visitor does next. */
export function clearBookingContext(): void {
    Sentry.setTags(Object.fromEntries([...Object.keys(context), 'request_id'].map((key) => [key, undefined])));
    Sentry.setContext('booking', null);
    context = {};
    lastRequestId = undefined;
}

export type BookingErrorReport = {
    /** Where in the flow: 'cart-load', 'seat-lock-sync', 'payment-intent', 'confirm-payment', ... */
    step: string;
    /** What went wrong, stable enough to group by: 'no-response', 'api-error', 'stripe-not-loaded', ... */
    code: string;
    /** The caught value, if there is one. An Error is captured as an exception, anything else as a message. */
    error?: unknown;
    level?: 'warning' | 'error';
    /** Overrides what `setBookingContext` knows, for server actions, which have no browser context. */
    context?: BookingContext;
    extra?: Record<string, unknown>;
};

/** Reports a failure the booking handles but the team must still see (Sentry). Never throws. */
export function captureBookingError({
    step,
    code,
    error,
    level = 'error',
    context: override,
    extra
}: BookingErrorReport) {
    try {
        const requestId = (error as ErrorLike | undefined)?.requestId ?? lastRequestId;
        const options = {
            level,
            tags: { flow: 'booking', step, code, ...definedTags({ ...context, ...override, request_id: requestId }) },
            extra: { ...extra, error: describeBookingError(error) }
        };
        if (error instanceof Error) {
            Sentry.captureException(error, options);
        } else {
            Sentry.captureMessage(`Booking error: ${step}/${code}`, options);
        }
    } catch {
        // Reporting must never fail a booking.
    }
}
